import React from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { Button, Icon, Image, VideoPlayer } from "@lululemon/ui-library";
import { playIcon } from "@lululemon/ui-icons";
import { isAdaptiveVideo } from "@lululemon/ecom-common/src/util/video-utils";
import { expandIcon } from "./reel-entry-point-icons";
import styles from "./reel-entry-point.module.scss";
import { getAnimateProgressBar, setBottomContentHeight } from "../utils";

export default function ReelEntryPoint({
  description,
  handleClickEntry,
  isReelForCarouselList,
  isTabletOrBelow,
  poster,
  storiesLength,
  title,
  url,
}) {
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const [isMouseOver, setIsMouseOver] = React.useState(false);
  const animationRef = React.useRef();
  const bottomContentRef = React.useRef();
  const descriptionRef = React.useRef();
  const progressBarRef = React.useRef();
  const videoLoadEventRef = React.useRef();
  const videoRef = React.useRef();

  const handleVideoRef = React.useCallback((videoEl) => {
    if (!videoEl) return;
    videoRef.current = videoEl;

    const handleVideoLoadData = () => {
      setIsVideoLoaded(true);
      const animationCallback = getAnimateProgressBar(
        videoRef.current,
        progressBarRef.current,
        animationRef
      );

      if (animationCallback) {
        animationRef.current = requestAnimationFrame(animationCallback);
      }
    };

    videoRef.current?.addEventListener("loadeddata", handleVideoLoadData);
    // Setting cleanUp callback
    videoLoadEventRef.current = () => {
      cancelAnimationFrame(animationRef.current);
      videoRef.current?.removeEventListener("loadeddata", handleVideoLoadData);
    };
  }, []);

  const updateBottomContentHeight = React.useCallback(() => {
    setBottomContentHeight(bottomContentRef?.current);
  }, [bottomContentRef]);

  React.useEffect(() => {
    if (bottomContentRef?.current) {
      window.addEventListener("resize", updateBottomContentHeight);
      updateBottomContentHeight();
    }

    return () => {
      window.removeEventListener("resize", updateBottomContentHeight);
    };
  }, [updateBottomContentHeight]);

  if ((!description, !title, !url)) {
    throw new Error("Feature Reels, Missing Entry point data");
  }

  const handleMouseOver = () => {
    setIsMouseOver(true);
    setTimeout(() => {
      setBottomContentHeight(
        bottomContentRef?.current,
        +descriptionRef?.current?.offsetHeight
      );
    }, 50);
  };

  const handleMouseLeave = () => {
    setIsVideoLoaded(false);
    setIsMouseOver(false);
    setTimeout(() => {
      updateBottomContentHeight();
    }, 50);
  };

  return (
    <div
      className={cs(styles.reelEntryPointWrapper, {
        [styles.reelEntryPointWrapperCarouselList]: isReelForCarouselList,
      })}
    >
      <Button
        aria-label={`Open carousel of ${storiesLength} slides in full screen`}
        className={styles.reelEntryPointButton}
        data-testid="entry-point-button"
        variant="transparent"
        onClick={handleClickEntry}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseOver}
        onBlur={handleMouseOver}
      >
        <div className={styles.reelEntryPointOverlay}>
          <div className={styles.reelEntryPointProgressBarContainer}>
            {[
              Array.from({ length: storiesLength }, (_, index) => index).map(
                (storyNumber) => (
                  <div
                    key={storyNumber}
                    className={styles.reelEntryPointProgressBar}
                  >
                    {storyNumber === 0 && isMouseOver && (
                      <div
                        ref={progressBarRef}
                        className={styles.reelEntryPointProgressBarAnimation}
                      />
                    )}
                  </div>
                )
              ),
            ]}
          </div>
          <div
            className={styles.reelEntryPointBottomContent}
            ref={bottomContentRef}
          >
            <div
              className={cs(styles.reelEntryPointPlayIconCircle, {
                [styles.reelEntryPointPlayIconCircleHover]: isMouseOver,
              })}
              data-testid="reel-entry-point-icon"
            >
              <Icon
                className={styles.reelEntryPointPlayIcon}
                content={
                  isMouseOver && !isTabletOrBelow ? expandIcon : playIcon
                }
              />
            </div>
            <div
              className={cs(styles.reelEntryPointText, {
                [styles.reelEntryPointTextMouseOver]: isMouseOver,
              })}
            >
              {title}
            </div>
            <div
              className={styles.reelEntryPointDescription}
              ref={descriptionRef}
            >
              {description}
            </div>
          </div>
        </div>
        <div className={styles.reelEntryPointMediaWrapper}>
          {!isTabletOrBelow && isMouseOver && (
            <VideoPlayer
              allowAudio={false}
              allowFullscreen={false}
              autoplay
              className={cs(styles.reelEntryPointVideoWrapper, {
                [styles.reelEntryPointVideoWrapperReady]: isVideoLoaded,
              })}
              videoClassName={styles.reelEntryPointVideo}
              data-testid="entry-point-video"
              landscapeAdaptiveSource={isAdaptiveVideo(url) ? url : undefined}
              landscapeSource={!isAdaptiveVideo(url) ? url : undefined}
              landscapePosterImageProps={{
                alt: title,
                src: poster,
              }}
              loop
              playPauseButtonClassName={styles.reelEntryPointPlayPauseButton}
              prefetch={false}
              ref={handleVideoRef}
              showDuration={false}
            />
          )}
          <Image
            alt={title}
            className={styles.reelEntryPointImgWrapper}
            draggable={false}
            lazy={false}
            src={poster}
          />
        </div>
      </Button>
    </div>
  );
}

ReelEntryPoint.defaultProps = {
  isReelForCarouselList: false,
};

ReelEntryPoint.propTypes = {
  description: PropTypes.string.isRequired,
  handleClickEntry: PropTypes.func.isRequired,
  isReelForCarouselList: PropTypes.bool,
  isTabletOrBelow: PropTypes.bool.isRequired,
  poster: PropTypes.string.isRequired,
  storiesLength: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};
